import { useRef, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

import { markdownClasses } from './classes';
import { normalizePieChart } from './mermaid-pie';
import { normalizeXyChart } from './mermaid-xychart';
import { normalizeFlowchart } from './mermaid-flowchart';
import { normalizeQuadrantChart } from './mermaid-quadrant';

// ----------------------------------------------------------------------

// mermaid is a large dependency (500KB+ parsed) — load it once, lazily, only
// when a diagram actually needs to render, and share the import across every
// diagram instance on the page instead of bundling it into the main chunk.
/** @type {Promise<typeof import('mermaid').default> | undefined} */
let mermaidPromise;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid')
      .then((mod) => mod.default ?? mod)
      .catch((err) => {
        mermaidPromise = undefined;
        throw err;
      });
  }
  return mermaidPromise;
}

// mermaid's xychart-beta lays out categories within a fixed chart width (700px by
// default) no matter how many there are, so a chart with a couple dozen x-axis
// categories packs them so tightly the labels collide. Widen the chart per category
// and rotate the labels so they take vertical rather than horizontal space.
const XYCHART_MIN_WIDTH = 700;
const XYCHART_WIDTH_PER_CATEGORY = 60;
const XYCHART_LABEL_ROTATION_THRESHOLD = 8;

/** @param {string} chart */
function getXyChartConfig(chart) {
  const match = chart.match(/x-axis\s*\[([^\]]*)\]/);
  if (!match) return undefined;

  const categoryCount = match[1].split(',').filter((/** @type {string} */ entry) => entry.trim()).length;
  if (categoryCount === 0) return undefined;

  return {
    width: Math.max(XYCHART_MIN_WIDTH, categoryCount * XYCHART_WIDTH_PER_CATEGORY),
    xAxis: {
      labelRotation: categoryCount > XYCHART_LABEL_ROTATION_THRESHOLD ? -45 : 0,
    },
  };
}

let diagramCounter = 0;

export function MermaidDiagram({ chart }) {
  const theme = useTheme();
  const [svg, setSvg] = useState(null);
  const [error, setError] = useState(null);
  const idRef = useRef(`mermaid-diagram-${++diagramCounter}`);

  const { main: primaryMain } = theme.palette.primary;
  const { fontFamily } = theme.typography;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSvg(null);

    loadMermaid()
      .then(async (mermaid) => {
        if (cancelled) return;

        // mermaid keeps its config as module-level state rather than a per-call
        // option, so re-initialize immediately before every render — this keeps
        // each diagram in sync with the current theme (light/dark) even though
        // multiple diagrams may exist on the page.
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          // On a parse error mermaid otherwise renders its "Syntax error" bomb SVG into
          // document.body and leaves it there; we show our own fallback instead.
          suppressErrorRendering: true,
          theme: theme.palette.mode === 'dark' ? 'dark' : 'default',
          themeVariables: {
            fontFamily,
            primaryColor: theme.palette.primary.lighter,
            primaryTextColor: theme.palette.text.primary,
            primaryBorderColor: primaryMain,
            lineColor: theme.palette.text.secondary,
            secondaryColor: theme.palette.background.neutral,
            tertiaryColor: theme.palette.background.paper,
          },
          xyChart: getXyChartConfig(chart),
        });

        const source = normalizePieChart(normalizeFlowchart(normalizeXyChart(normalizeQuadrantChart(chart))));
        const { svg: renderedSvg } = await mermaid.render(idRef.current, source);
        if (!cancelled) setSvg(renderedSvg);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to render diagram');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, theme.palette.mode, primaryMain, fontFamily]);

  if (error) {
    return (
      <div className={markdownClasses.content.codeBlock}>
        <Alert severity="warning" sx={{ mb: 1 }}>
          Diagram could not be rendered ({error}); showing raw source below.
        </Alert>
        <pre>
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <Box
        sx={{
          my: 1.5,
          minHeight: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          typography: 'body2',
        }}
      >
        Rendering diagram…
      </Box>
    );
  }

  return (
    <Box
      className={markdownClasses.content.mermaid}
      sx={{
        my: 1.5,
        display: 'flex',
        justifyContent: 'center',
        overflowX: 'auto',
        '& svg': { maxWidth: '100%', height: 'auto' },
      }}
      // mermaid's own SVG output — sanitized upstream via securityLevel: 'strict',
      // not user/LLM-controlled markup passed through verbatim.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
